import React, { useState, useEffect } from 'react';
import { UserPlus, Trash2, KeyRound, Loader2, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { API, useAuth } from '@/App';

const AdminUsers = () => {
  const { admin } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [newUser, setNewUser] = useState({ name: '', email: '', password: '' });
  const [saving, setSaving] = useState(false);
  const [passwordEdit, setPasswordEdit] = useState(null);
  const [newPassword, setNewPassword] = useState('');

  const headers = { Authorization: `Bearer ${admin.token}` };

  useEffect(() => { fetchUsers(); }, []);

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${API}/admin/users`, { headers });
      setUsers(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!newUser.name || !newUser.email || !newUser.password) {
      toast.error('Alle Felder ausfüllen');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/admin/register`, newUser, { headers });
      toast.success('Benutzer erstellt!');
      setNewUser({ name: '', email: '', password: '' });
      setShowAdd(false);
      fetchUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Erstellen');
    } finally { setSaving(false); }
  };

  const handleDelete = async (user) => {
    if (user.id === admin.id) {
      toast.error('Du kannst dich nicht selbst löschen');
      return;
    }
    if (!window.confirm(`"${user.name}" wirklich löschen?`)) return;
    try {
      await axios.delete(`${API}/admin/users/${user.id}`, { headers });
      toast.success('Benutzer gelöscht');
      fetchUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Löschen');
    }
  };

  const handlePasswordChange = async (userId) => {
    if (!newPassword || newPassword.length < 4) {
      toast.error('Passwort muss mindestens 4 Zeichen haben');
      return;
    }
    try {
      await axios.put(`${API}/admin/users/${userId}/password`, { password: newPassword }, { headers });
      toast.success('Passwort geändert!');
      setPasswordEdit(null);
      setNewPassword('');
    } catch (e) {
      toast.error('Fehler beim Ändern');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div data-testid="admin-users-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Benutzerverwaltung</h1>
          <p className="text-slate-500 mt-1">{users.length} Admin-Benutzer</p>
        </div>
        <Button onClick={() => setShowAdd(!showAdd)} data-testid="add-user-btn">
          <UserPlus className="w-4 h-4 mr-2" />
          Neuer Benutzer
        </Button>
      </div>

      {showAdd && (
        <Card className="mb-6">
          <CardContent className="p-6">
            <h3 className="text-lg font-semibold mb-4">Neuen Admin-Benutzer anlegen</h3>
            <form onSubmit={handleAdd} className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label>Name</Label>
                <Input
                  value={newUser.name}
                  onChange={(e) => setNewUser(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Max Mustermann"
                  data-testid="new-user-name"
                />
              </div>
              <div>
                <Label>E-Mail</Label>
                <Input
                  type="email"
                  value={newUser.email}
                  onChange={(e) => setNewUser(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="max@roessnerfahrschule.de"
                  data-testid="new-user-email"
                />
              </div>
              <div>
                <Label>Passwort</Label>
                <Input
                  type="password"
                  value={newUser.password}
                  onChange={(e) => setNewUser(prev => ({ ...prev, password: e.target.value }))}
                  placeholder="Passwort"
                  data-testid="new-user-password"
                />
              </div>
              <div className="md:col-span-3 flex gap-2">
                <Button type="submit" disabled={saving} data-testid="save-user-btn">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Erstellen
                </Button>
                <Button type="button" variant="outline" onClick={() => setShowAdd(false)}>
                  Abbrechen
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      <div className="space-y-3">
        {users.map(user => (
          <Card key={user.id} data-testid={`user-card-${user.id}`}>
            <CardContent className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                  <Users className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <div className="font-semibold text-slate-900">{user.name}</div>
                  <div className="text-sm text-slate-500">{user.email}</div>
                </div>
                {user.id === admin.id && (
                  <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-medium">Du</span>
                )}
              </div>
              <div className="flex items-center gap-2">
                {passwordEdit === user.id ? (
                  <div className="flex items-center gap-2">
                    <Input
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Neues Passwort"
                      className="w-40 h-8 text-sm"
                      data-testid={`password-input-${user.id}`}
                    />
                    <Button size="sm" onClick={() => handlePasswordChange(user.id)} data-testid={`save-password-${user.id}`}>
                      Speichern
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => { setPasswordEdit(null); setNewPassword(''); }}>
                      Abbrechen
                    </Button>
                  </div>
                ) : (
                  <>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => { setPasswordEdit(user.id); setNewPassword(''); }}
                      data-testid={`change-password-${user.id}`}
                    >
                      <KeyRound className="w-4 h-4 mr-1" />
                      Passwort
                    </Button>
                    {user.id !== admin.id && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-red-600 hover:bg-red-50"
                        onClick={() => handleDelete(user)}
                        data-testid={`delete-user-${user.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default AdminUsers;
